<script setup lang="ts">
/**
 * FormFieldRadios is a shorthand helper for creating one or more radios in a form.
 * It wraps our usual Form components, but providing a shorter API to them.
 */

import { vAutoAnimate } from '@formkit/auto-animate/vue';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, } from '../index';
import { RadioGroup, RadioGroupItem } from '../../radio-group'

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  name: string;
  description?: string;
  id?: string;
  label?: string;
  required?: boolean;
  options?: Array<{ title: string; value: string; }>;
}>();

const fieldName = computed(() => props.id || props.name);
</script>

<template>
  <FormField :name="fieldName" type="radio" v-slot="{ componentField }">
    <FormItem :id="fieldName" v-auto-animate>
      <FormLabel as="div">
        <slot />
        <template v-if="!$slots.default">
          {{ props.label }}<span v-if="props.label && props.required" class="label--required">*</span>
        </template>
      </FormLabel>

      <FormControl>
        <RadioGroup v-bind="componentField">
          <FormItem
            v-for="option in options"
            :key="option.value"
            :id="option.value"
            class="flex flex-row items-center"
          >
            <FormControl>
              <RadioGroupItem :value="option.value" />
            </FormControl>
            <FormLabel class="font-normal">
              {{ option.title }}
            </FormLabel>
          </FormItem>
        </RadioGroup>
      </FormControl>

      <FormDescription v-if="props.description || $slots.description">
        <template v-if="!$slots.description">
          {{ props.description }}
        </template>
        <slot name="description" />
      </FormDescription>

      <FormMessage />
    </FormItem>
  </FormField>
</template>
